// MorePage — Hub-based navigation refactor
// Replaces 50+ standalone menu items with 9 consolidated parent hubs.
// Each hub opens a beautiful iOS HIG page that organizes its sub-features.
// Top-level search finds any feature instantly.

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, TierPill } from '../../components/Brand';
import { getUser } from '../../lib/auth';
import { DEMO_CHALLENGE } from '../../data/platforms';

interface Hub {
  id: string;
  to: string;
  icon: string;
  iconBg: string;     // linear-gradient seed
  label: string;
  sub: string;
  badge?: { text: string; color: string; bg: string };
  // list of routes reachable from this hub — used for search
  items: { to: string; label: string; sub: string }[];
}

const HUBS: Hub[] = [
  {
    id: 'payments',
    to: '/portal/hub/payments',
    icon: '💰', iconBg: 'linear-gradient(135deg, #34C759 0%, #248A3D 100%)',
    label: 'Payments', sub: 'Wallet, deposits, payouts, statements',
    badge: { text: '$2.4K', color: 'white', bg: '#34C759' },
    items: [
      { to: '/portal/hub/payments',         label: 'Wallet overview',         sub: 'Balance + recent activity' },
      { to: '/portal/deposit-flow',         label: 'Add funds',               sub: 'Deposit to wallet' },
      { to: '/portal/withdraw-flow',        label: 'Withdraw',                sub: 'To bank / crypto' },
      { to: '/portal/payout-methods',       label: 'Payout methods',          sub: 'Bank, crypto, PayPal' },
      { to: '/portal/statements',           label: 'Statements & tax',        sub: 'Monthly, tax docs' },
      { to: '/portal/tier-checkout',        label: 'Subscription plan',       sub: 'Compare tiers' },
    ],
  },
  {
    id: 'account',
    to: '/portal/hub/account',
    icon: '👤', iconBg: 'linear-gradient(135deg, #007AFF 0%, #0040DD 100%)',
    label: 'Account', sub: 'Profile, KYC, region, about',
    items: [
      { to: '/portal/hub/account',          label: 'Account overview',        sub: 'Identity, KYC, status' },
      { to: '/portal/profile-edit',         label: 'Edit profile',            sub: 'Name, bio, photo' },
      { to: '/portal/kyc-flow',             label: 'Verify identity (KYC)',   sub: 'Required for payouts' },
      { to: '/portal/country',              label: 'Country & tax',           sub: 'Region, currency, residency' },
      { to: '/portal/about',                label: 'About Funder Pro',        sub: 'Version 3.2.1' },
    ],
  },
  {
    id: 'security',
    to: '/portal/hub/security',
    icon: '🔐', iconBg: 'linear-gradient(135deg, #FF3B30 0%, #B82B22 100%)',
    label: 'Security & Privacy', sub: '2FA, sessions, activity log',
    badge: { text: 'Action', color: 'white', bg: '#FF3B30' },
    items: [
      { to: '/portal/hub/security',         label: 'Security overview',       sub: '2FA, devices, alerts' },
      { to: '/portal/2fa-setup',            label: 'Setup 2FA',               sub: 'Authenticator app' },
      { to: '/portal/security',             label: 'Security center',         sub: 'Sessions, login alerts' },
      { to: '/portal/activity',             label: 'Activity log',            sub: 'All account actions' },
      { to: '/portal/notifications-settings', label: 'Notifications',         sub: 'Push, email, SMS, in-app' },
    ],
  },
  {
    id: 'ai',
    to: '/portal/hub/ai',
    icon: '🤖', iconBg: 'linear-gradient(135deg, #5856D6 0%, #3634A3 100%)',
    label: 'AI & Personalization', sub: 'Bot, strategies, automation',
    badge: { text: 'Live', color: 'white', bg: '#5856D6' },
    items: [
      { to: '/portal/hub/ai',               label: 'AI overview',             sub: 'Bot status + performance' },
      { to: '/portal/bot',                  label: 'AI bot',                  sub: 'Live trading bot' },
      { to: '/portal/bot-config',           label: 'Bot configuration',       sub: 'Strategy, risk, pairs' },
      { to: '/portal/bot-marketplace',      label: 'Bot marketplace',         sub: '8 community strategies' },
      { to: '/portal/calculator',           label: 'Position calculator',     sub: 'Risk & contract size' },
      { to: '/portal/backtest',             label: 'Backtest lab',            sub: 'Test strategies', sub2: 'Pro only' },
    ] as any,
  },
  {
    id: 'orders',
    to: '/portal/hub/orders',
    icon: '📊', iconBg: 'linear-gradient(135deg, #FF9500 0%, #C93400 100%)',
    label: 'Orders & Activity', sub: 'Trades, journal, analytics',
    items: [
      { to: '/portal/hub/orders',           label: 'Activity overview',       sub: 'Recent + summary' },
      { to: '/portal/orders',               label: 'Orders',                  sub: 'All trades' },
      { to: '/portal/trades',               label: 'Trade history',           sub: 'Closed positions' },
      { to: '/portal/journal',              label: 'Trade journal',           sub: '30-day heatmap + notes' },
      { to: '/portal/analytics',            label: 'Analytics',               sub: 'P&L, win rate, drawdown' },
      { to: '/portal/leaderboard',          label: 'Leaderboard',             sub: 'Top traders this week' },
      { to: '/portal/achievements',         label: 'Achievements',            sub: 'Badges & milestones' },
      { to: '/portal/certificates',          label: 'Certificates',            sub: 'Trophy case' },
      { to: '/portal/showcase',              label: 'Showcase',                sub: 'Public profile' },
    ],
  },
  {
    id: 'risk',
    to: '/portal/hub/risk',
    icon: '🛡️', iconBg: 'linear-gradient(135deg, #1C1C1E 0%, #48484A 100%)',
    label: 'Risk Center', sub: 'Limits, rules, news, market data',
    items: [
      { to: '/portal/hub/risk',             label: 'Risk overview',           sub: 'Live metrics dashboard' },
      { to: '/portal/risk-center',          label: 'Risk dashboard',          sub: 'Score + metrics' },
      { to: '/portal/margin',               label: 'Margin monitor',          sub: '30% per trade limit' },
      { to: '/portal/auto-flatten',         label: 'Auto-flatten',            sub: 'EOD timer' },
      { to: '/portal/daily-pause',          label: 'Daily Pause',             sub: 'Section 13.9' },
      { to: '/portal/news-blackouts',       label: 'News blackouts',          sub: '2-min rule events' },
      { to: '/portal/trading-rules',        label: 'Trading rules',           sub: 'Section 13' },
      { to: '/portal/market-calendar',      label: 'Market calendar',         sub: 'Economic events' },
      { to: '/portal/brokers',              label: 'Brokers & data feeds',    sub: 'Connections' },
    ],
  },
  {
    id: 'challenges',
    to: '/portal/hub/challenges',
    icon: '🎯', iconBg: 'linear-gradient(135deg, #FFD60A 0%, #FF9500 100%)',
    label: 'Challenges', sub: 'Pricing, scaling, reset, rewards',
    items: [
      { to: '/portal/hub/challenges',       label: 'Challenges overview',     sub: 'Active + plans' },
      { to: '/portal/challenge-pricing',    label: 'Challenge pricing',       sub: 'Compare 3 tiers' },
      { to: '/portal/scaling',              label: 'Scaling plan',            sub: 'Profit → more power' },
      { to: '/portal/reset-challenge',      label: 'Reset challenge',         sub: 'Anytime, keep days' },
      { to: '/portal/promotions',           label: 'Promotions & codes',      sub: 'Gold offer, referral' },
      { to: '/portal/referrals',            label: 'Referral program',        sub: 'Invite friends' },
    ],
  },
  {
    id: 'support',
    to: '/portal/hub/support',
    icon: '💬', iconBg: 'linear-gradient(135deg, #5856D6 0%, #FF2D55 100%)',
    label: 'Support', sub: 'Help, FAQ, contact, legal',
    badge: { text: '24/7', color: 'white', bg: '#5856D6' },
    items: [
      { to: '/portal/hub/support',          label: 'Support overview',        sub: 'FAQ + live chat' },
      { to: '/portal/faq-center',           label: 'FAQ center',              sub: 'All categories' },
      { to: '/portal/help-center',          label: 'Help articles',           sub: 'Guides & walkthroughs' },
      { to: '/portal/messages',             label: 'Live chat',               sub: 'Talk to support' },
      { to: '/portal/feedback',             label: 'Send feedback',           sub: 'Help us improve' },
      { to: '/portal/disclosures',          label: 'Disclosures',             sub: 'Terms, Privacy, Risk' },
    ],
  },
  {
    id: 'settings',
    to: '/portal/hub/settings',
    icon: '⚙️', iconBg: 'linear-gradient(135deg, #8E8E93 0%, #48484A 100%)',
    label: 'Settings', sub: 'Subscription, API, advanced',
    items: [
      { to: '/portal/hub/settings',         label: 'Settings overview',       sub: 'Subscription + API' },
      { to: '/portal/subscription',         label: 'Subscription & billing',  sub: 'Plan & payment' },
      { to: '/portal/api-keys',             label: 'API keys',                sub: 'Pro only' },
      { to: '/portal/notifications-settings', label: 'Notifications',         sub: 'Channels + frequency' },
      { to: '/portal/feed',                 label: 'Social feed',             sub: 'Posts & copy-trade' },
      { to: '/portal/academy',              label: 'Academy',                 sub: 'Learn trading' },
    ],
  },
];

export function MorePage() {
  const nav = useNavigate();
  const user = getUser();
  const displayName = user?.full_name || 'Brandon';
  const userTier = (user as any)?.tier || DEMO_CHALLENGE.tier;
  const [query, setQuery] = useState('');

  // search across all hubs + their items
  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const matches: { hub: Hub; item?: { to: string; label: string; sub: string } }[] = [];
    for (const hub of HUBS) {
      const hubMatches = hub.label.toLowerCase().includes(q) || hub.sub.toLowerCase().includes(q);
      if (hubMatches) {
        matches.push({ hub });
      }
      // Always check items too, so users see all relevant matches
      for (const item of hub.items) {
        if (item.label.toLowerCase().includes(q) || item.sub.toLowerCase().includes(q)) {
          matches.push({ hub, item });
        }
      }
    }
    return matches;
  }, [query]);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      {/* Nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-center border-b border-tf-border ios26-navbar backdrop-blur-xl">
        <div className="text-[15px] font-semibold">More</div>
      </div>

      {/* Profile card */}
      <button
        onClick={() => nav('/portal/hub/account')}
        data-testid="more-profile-card"
        className="mx-4 mt-3 flex w-[calc(100%-2rem)] items-center gap-3 rounded-[16px] bg-white p-3 active:scale-[0.99] transition"
      >
        <UserAvatar size={52} name={displayName} />
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[16px] font-bold text-tf-text-1">{displayName}</div>
          <div className="text-[12px] text-tf-text-3">demo@funderpro.live</div>
          <div className="mt-0.5"><TierPill tier={userTier} /></div>
        </div>
        <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>

      {/* Search */}
      <div className="mx-4 mt-3 relative" data-testid="more-search">
        <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search any feature…"
          data-testid="more-search-input"
          className="w-full h-9 rounded-[10px] bg-white border border-tf-border pl-9 pr-3 text-[14px] text-tf-text-1 placeholder:text-tf-text-3 focus:outline-none focus:border-[#007AFF]"
        />
        {query && (
          <button onClick={() => setQuery('')} data-testid="more-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-[#E5E5EA] text-tf-text-3 flex items-center justify-center text-[12px] active:opacity-40">✕</button>
        )}
      </div>

      {/* Search results */}
      {searchResults && (
        <div data-testid="more-search-results">
          {searchResults.length === 0 ? (
            <div className="mx-4 mt-4 rounded-[16px] bg-white p-6 text-center">
              <div className="text-[40px] mb-2">🔍</div>
              <h3 className="text-[14px] font-bold text-tf-text-1">No matches</h3>
              <p className="text-[11px] text-tf-text-3 mt-1">Try searching for "wallet", "KYC", "bot", or "rules"</p>
            </div>
          ) : (
            <div className="mx-4 mt-3 space-y-3">
              {searchResults.map(({ hub, item }, i) => (
                <div key={`${hub.id}-${i}`}>
                  {item ? (
                    <button
                      onClick={() => nav(item.to)}
                      data-testid={`search-${item.to.replace(/\//g, '-')}`}
                      className="w-full rounded-[12px] border border-tf-border bg-white p-3 text-left active:scale-[0.99] transition flex items-center gap-3"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-[8px] text-[16px]" style={{ background: hub.iconBg }}>{hub.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-tf-text-1">{item.label}</div>
                        <div className="text-[11px] text-tf-text-3">{item.sub} · in {hub.label}</div>
                      </div>
                      <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => nav(hub.to)}
                      data-testid={`search-hub-${hub.id}`}
                      className="w-full rounded-[12px] border-2 border-[#007AFF] bg-white p-3 text-left active:scale-[0.99] transition flex items-center gap-3"
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-[8px] text-[16px]" style={{ background: hub.iconBg }}>{hub.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-bold text-tf-text-1">{hub.label} hub</div>
                        <div className="text-[11px] text-tf-text-3">{hub.sub}</div>
                      </div>
                      <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-blue" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mx-4 mt-2 text-center text-[10px] text-tf-text-3">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</div>
        </div>
      )}

      {/* Hub grid (default view) */}
      {!searchResults && (
        <>
          <h2 className="mt-5 px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-tf-text-3">Categories</h2>
          <div className="mx-4 grid grid-cols-2 gap-2.5" data-testid="more-hub-grid">
            {HUBS.map((hub) => (
              <button
                key={hub.id}
                onClick={() => nav(hub.to)}
                data-testid={`hub-${hub.id}`}
                className="relative rounded-[16px] bg-white p-3.5 text-left active:scale-[0.98] transition overflow-hidden min-h-[110px] flex flex-col"
              >
                {/* gradient header bar */}
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: hub.iconBg }} />
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-[10px] text-[22px] mb-2"
                  style={{ background: hub.iconBg }}
                >
                  {hub.icon}
                </div>
                <div className="text-[14px] font-bold text-tf-text-1 leading-tight">{hub.label}</div>
                <div className="text-[10.5px] text-tf-text-3 leading-snug mt-0.5 line-clamp-2">{hub.sub}</div>
                {hub.badge && (
                  <div
                    className="absolute top-2.5 right-2.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase"
                    style={{ backgroundColor: hub.badge.bg, color: hub.badge.color }}
                  >
                    {hub.badge.text}
                  </div>
                )}
              </button>
            ))}
          </div>

          <p className="mt-4 mx-4 text-center text-[10px] text-tf-text-3">
            9 hubs · {HUBS.reduce((s, h) => s + h.items.length, 0)} features · v3.2.1
          </p>
        </>
      )}
    </div>
  );
}
